import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls, addEdge,
  useNodesState, useEdgesState, MarkerType, useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { ArrowLeft, FloppyDisk, Play, MagnifyingGlass, SquaresFour, Sliders, X, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { BLOCKS, CATEGORIES, blockDef, triggerLabel } from "./blocks";
import BuilderNode from "./BuilderNode";
import ConfigPanel from "./ConfigPanel";
import SimulateModal from "./SimulateModal";

const nodeTypes = { dishenda: BuilderNode };
let idCounter = 0;
const nextId = () => `n_${Date.now()}_${idCounter++}`;

function legacyToGraph(auto) {
  const nodes = [
    { id: "n1", type: "dishenda", position: { x: 260, y: 0 },
      data: { category: "trigger", kind: auto.trigger, label: triggerLabel(auto.trigger), config: {} } },
  ];
  const edges = [];
  let y = 150;
  let prev = "n1";
  if ((auto.delay_days || 0) > 0) {
    nodes.push({ id: "n2", type: "dishenda", position: { x: 260, y },
      data: { category: "wait", kind: "attesa", label: "Attesa", config: { unit: "giorni", value: auto.delay_days } } });
    edges.push({ id: "e1", source: prev, target: "n2" });
    prev = "n2"; y += 150;
  }
  const hasAction = auto.action && auto.action !== "workflow";
  if (hasAction) {
    const actId = `n${nodes.length + 1}`;
    nodes.push({ id: actId, type: "dishenda", position: { x: 260, y },
      data: { category: "action", kind: auto.action, label: auto.action.replaceAll("_", " "),
        config: { body: auto.message_template || "", survey_id: auto.survey_id, tag: auto.tag } } });
    edges.push({ id: `e_${prev}_${actId}`, source: prev, target: actId });
    prev = actId;
  }
  const finId = `n${nodes.length + 1}`;
  nodes.push({ id: finId, type: "dishenda", position: { x: 260, y: y + 150 },
    data: { category: "action", kind: "fine", label: "Fine", config: {} } });
  edges.push({ id: `e_${prev}_${finId}`, source: prev, target: finId });
  return { nodes, edges };
}

function Palette({ onAdd, visible, onClose }) {
  const [q, setQ] = useState("");
  const [openCat, setOpenCat] = useState("trigger");
  const filtered = BLOCKS.filter((b) =>
    b.label.toLowerCase().includes(q.toLowerCase()) || b.kind.includes(q.toLowerCase()));

  return (
    <div className={`${visible ? "flex" : "hidden"} md:flex w-64 shrink-0 flex-col bg-white border-r border-zinc-200 absolute md:static z-20 inset-y-0 left-0`}
      data-testid="block-palette">
      <div className="p-3 border-b border-zinc-200 flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca blocco..."
            data-testid="palette-search"
            className="w-full h-9 pl-8 pr-2 rounded-md border border-zinc-300 text-xs focus:outline-none focus:ring-2 focus:ring-zinc-900" />
        </div>
        <button onClick={onClose} className="md:hidden p-1.5 text-zinc-500"><X size={16} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {Object.entries(CATEGORIES).map(([catKey, cat]) => {
          const items = filtered.filter((b) => b.category === catKey);
          if (items.length === 0) return null;
          return (
            <div key={catKey} className="mb-2">
              <button onClick={() => setOpenCat(openCat === catKey ? "" : catKey)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-[10px] uppercase tracking-wider font-bold"
                style={{ color: cat.color }} data-testid={`palette-cat-${catKey}`}>
                {cat.label} <span className="text-zinc-400">({items.length})</span>
              </button>
              {(openCat === catKey || q) && items.map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.kind} draggable
                    onDragStart={(e) => e.dataTransfer.setData("application/dishenda", JSON.stringify({ category: b.category, kind: b.kind }))}
                    onClick={() => onAdd(b)}
                    data-testid={`palette-block-${b.kind}`}
                    className="flex items-center gap-2 px-2 py-2 rounded-md cursor-grab hover:bg-zinc-50 border border-transparent hover:border-zinc-200 transition mb-0.5">
                    <span className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${cat.color}18` }}>
                      <Icon size={15} weight="bold" style={{ color: cat.color }} />
                    </span>
                    <span className="text-xs font-medium text-zinc-800 leading-tight">{b.label}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BuilderInner({ automation, onBack, onSaved }) {
  const initial = useMemo(() => {
    if (automation?.nodes?.length) {
      const tcfg = automation.trigger_config || {};
      return {
        nodes: automation.nodes.map((n) => ({
          ...n, type: "dishenda",
          // il legame col coupon vive in trigger_config: lo riverso nel config del nodo
          // trigger, cosi' il pannello lo mostra e il salvataggio lo preserva
          data: n.data?.category === "trigger"
            ? { ...n.data, config: { ...tcfg, ...(n.data.config || {}) } }
            : n.data,
        })),
        edges: (automation.edges || []).map((e) => ({
          ...e, type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed },
        })),
      };
    }
    if (automation) return legacyToGraph(automation);
    return { nodes: [], edges: [] };
  }, [automation]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initial.edges);
  const [name, setName] = useState(automation?.name || "Nuova automazione");
  const [active, setActive] = useState(automation?.active || false);
  const [autoId, setAutoId] = useState(automation?.id || null);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSimulate, setShowSimulate] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const wrapper = useRef(null);
  const { screenToFlowPosition, setCenter } = useReactFlow();

  const hasTrigger = nodes.some((n) => n.data.category === "trigger");
  const selected = nodes.find((n) => n.id === selectedId) || null;

  const addBlock = useCallback((block, position) => {
    if (block.category === "trigger" && nodes.some((n) => n.data.category === "trigger")) {
      toast.error("Il workflow può avere un solo trigger");
      return;
    }
    const def = blockDef(block.category, block.kind);
    const id = nextId();
    let pos = position;
    if (!pos) {
      const bounds = wrapper.current?.getBoundingClientRect();
      pos = bounds
        ? screenToFlowPosition({ x: bounds.left + bounds.width / 2 - 104, y: bounds.top + bounds.height / 3 })
        : { x: 260, y: 120 };
    }
    setNodes((ns) => ns.concat({
      id, type: "dishenda", position: pos,
      data: { category: block.category, kind: block.kind, label: def?.label || block.kind, config: { ...(def?.defaults || {}) } },
    }));
    setSelectedId(id);
    setConfigOpen(true);
    if (!position) {
      setTimeout(() => setCenter(pos.x + 104, pos.y + 40, { zoom: 1, duration: 300 }), 80);
    }
  }, [nodes, setNodes, screenToFlowPosition, setCenter]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    try {
      const block = JSON.parse(e.dataTransfer.getData("application/dishenda"));
      const bounds = wrapper.current.getBoundingClientRect();
      addBlock(block, screenToFlowPosition({ x: e.clientX - bounds.left, y: e.clientY - bounds.top }));
    } catch { /* ignore */ }
  }, [addBlock, screenToFlowPosition]);

  const onConnect = useCallback((conn) => {
    setEdges((eds) => addEdge({ ...conn, type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed } }, eds));
  }, [setEdges]);

  const updateNode = useCallback((id, config, label) => {
    setNodes((ns) => ns.map((n) => n.id === id
      ? { ...n, data: { ...n.data, config, ...(label !== undefined ? { label } : {}) } } : n));
  }, [setNodes]);

  const deleteNode = useCallback((id) => {
    setNodes((ns) => ns.filter((n) => n.id !== id));
    setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
    setSelectedId(null);
  }, [setNodes, setEdges]);

  const save = async () => {
    if (!hasTrigger) { toast.error("Aggiungi un blocco Trigger per iniziare il flusso"); return null; }
    setSaving(true);
    const payload = {
      name, active,
      nodes: nodes.map((n) => ({ id: n.id, type: "dishenda", position: n.position, data: n.data })),
      edges: edges.map((e) => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle || null })),
    };
    try {
      const r = autoId
        ? await api.put(`/automations/${autoId}/graph`, payload)
        : await api.post("/automations/graph-create", payload);
      setAutoId(r.data.id);
      toast.success("Workflow salvato");
      onSaved?.();
      return r.data.id;
    } catch (err) {
      toast.error(apiError(err));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const simulate = async () => {
    const id = await save();
    if (id) setShowSimulate(true);
  };

  return (
    <div className="fixed inset-0 z-40 bg-zinc-100 flex flex-col" data-testid="workflow-builder">
      <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 min-h-14 py-1.5 flex-wrap bg-white border-b border-zinc-200 shrink-0">
        <button onClick={onBack} data-testid="builder-back" className="p-2 rounded-md text-zinc-500 hover:bg-zinc-100">
          <ArrowLeft size={18} />
        </button>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="builder-name"
          className="font-display font-bold text-zinc-900 text-sm sm:text-base bg-transparent focus:outline-none border-b border-transparent focus:border-zinc-300 min-w-0 w-32 sm:w-auto sm:max-w-xs" />
        <button onClick={() => setActive(!active)} data-testid="builder-active"
          className={`h-8 px-2.5 rounded-full text-[11px] font-bold border transition ${active ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-zinc-100 text-zinc-500 border-zinc-300"}`}>
          {active ? "ATTIVA" : "DISATTIVA"}
        </button>
        <div className="flex-1" />
        <button onClick={() => setPaletteOpen(true)} className="md:hidden p-2 rounded-md text-zinc-600 border border-zinc-200" data-testid="builder-open-palette">
          <SquaresFour size={18} />
        </button>
        {selected && (
          <button onClick={() => setConfigOpen(true)} className="md:hidden p-2 rounded-md text-zinc-600 border border-zinc-200" data-testid="builder-open-config">
            <Sliders size={18} />
          </button>
        )}
        {autoId && (
          <button onClick={() => {
              api.get(`/automations/${autoId}/versions`).then((r) => { setVersions(r.data); setShowVersions(true); }).catch(() => {});
            }} data-testid="builder-versions"
            className="h-10 px-2.5 rounded-md bg-white border border-zinc-300 text-xs font-bold text-zinc-700 flex items-center gap-1.5 hover:bg-zinc-50"
            title="Versioni salvate">
            <ClockCounterClockwise size={14} weight="bold" /> <span className="hidden sm:inline">Versioni</span>
          </button>
        )}
        <button onClick={simulate} data-testid="builder-simulate"
          className="h-10 px-2.5 sm:px-4 rounded-md bg-white border border-zinc-300 text-xs font-bold text-zinc-700 flex items-center gap-1.5 hover:bg-zinc-50">
          <Play size={14} weight="bold" /> <span className="hidden sm:inline">Simula</span>
        </button>
        <button onClick={save} disabled={saving} data-testid="builder-save"
          className="h-10 px-3 sm:px-4 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5 hover:bg-zinc-800 active:scale-95 transition disabled:opacity-50">
          <FloppyDisk size={14} weight="bold" /> {saving ? "..." : "Salva"}
        </button>
      </div>

      <div className="flex flex-1 min-h-0 relative">
        <Palette visible={paletteOpen} onClose={() => setPaletteOpen(false)} onAdd={(b) => { addBlock(b); setPaletteOpen(false); }} />
        <div className="flex-1" ref={wrapper} onDrop={onDrop} onDragOver={(e) => e.preventDefault()} data-testid="workflow-canvas">
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            onConnect={onConnect} nodeTypes={nodeTypes}
            onNodeClick={(_, node) => { setSelectedId(node.id); setConfigOpen(true); }}
            onPaneClick={() => setSelectedId(null)}
            deleteKeyCode={["Backspace", "Delete"]}
            fitView minZoom={0.3} maxZoom={1.6} proOptions={{ hideAttribution: true }}
          >
            <Background gap={20} color="#d4d4d8" />
            <Controls showInteractive={false} />
          </ReactFlow>
          {nodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center max-w-xs">
                <div className="font-display font-bold text-zinc-400 text-lg">Trascina un Trigger per iniziare</div>
                <p className="text-xs text-zinc-400 mt-1">Poi aggiungi attese, condizioni e azioni. Collega i blocchi trascinando dai punti di connessione.</p>
              </div>
            </div>
          )}
        </div>
        {selected && (
          <div className={`${configOpen ? "absolute inset-y-0 right-0 z-20" : "hidden"} md:static md:block w-80 shrink-0`}>
            <ConfigPanel node={selected} onChange={updateNode} onDelete={deleteNode}
              onClose={() => setConfigOpen(false)} currentAutoId={autoId}
              linkedCouponId={nodes.find((n) => n.data.category === "trigger")?.data?.config?.coupon_id || null} />
          </div>
        )}
      </div>

      {showVersions && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setShowVersions(false)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-lg max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="versions-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
              <h3 className="font-display font-bold text-lg">Versioni salvate</h3>
              <button onClick={() => setShowVersions(false)} className="p-2 text-zinc-500" data-testid="versions-close"><X size={18} /></button>
            </div>
            <div className="divide-y divide-zinc-100 overflow-y-auto">
              {versions.length === 0 && <div className="py-8 text-center text-sm text-zinc-400">Nessuna versione precedente. Le versioni vengono create a ogni salvataggio.</div>}
              {versions.map((v) => (
                <div key={v.index} className="flex items-center gap-3 px-5 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-zinc-900 truncate">{v.name}</div>
                    <div className="text-xs text-zinc-400">{v.saved_at ? new Date(v.saved_at).toLocaleString("it-IT") : ""}</div>
                  </div>
                  <button onClick={async () => {
                      try {
                        const r = await api.post(`/automations/${autoId}/restore/${v.index}`);
                        setNodes(r.data.nodes.map((n) => ({ ...n, type: "dishenda" })));
                        setEdges((r.data.edges || []).map((e) => ({ ...e, type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed } })));
                        setName(r.data.name);
                        setShowVersions(false);
                        toast.success("Versione ripristinata");
                      } catch (err) { toast.error(apiError(err)); }
                    }} data-testid={`restore-${v.index}`}
                    className="h-9 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold">
                    Ripristina
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showSimulate && autoId && <SimulateModal automationId={autoId} onClose={() => setShowSimulate(false)} />}
    </div>
  );
}

export default function WorkflowBuilder(props) {
  return (
    <ReactFlowProvider>
      <BuilderInner {...props} />
    </ReactFlowProvider>
  );
}
